import React, { useEffect, useState, forwardRef, Fragment } from "react";
import FormItem from "antd/es/form/FormItem";
import { Badge, Icon, Tooltip, Col, Row, Typography, Button } from 'antd';
import Title from 'antd/es/typography/Title';
import TranslatedText from "translations/TranslatedText";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import isEqual from "lodash/isEqual";
import HanziTile from 'ReusableComponents/HanziTile'

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle, tileColor) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 ${grid}px 0 0`,

  // change background colour if dragging
  border: isDragging ? 'dotted #1890FF' : tileColor,

  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = isDraggingOver => ({
  border: isDraggingOver ? 'none' : 'none',
  display: 'flex',
  flexDirection: 'column',
  padding: grid,
  overflow: 'auto',
});

export default forwardRef((props: any, ref: any) => {
  const { form, inputData, questionsKey, language, onCheckAnswer, checkAnswer } = props,
    { required, questions, id, defaultValue, disarrangedText: defaultDisarrangedText } = inputData,
    { getFieldDecorator } = form;
  const [tileColor, setTileColor] = useState('dotted #AAAAAA');
  const [arranged, setArranged] = useState([]);
  const [answer, setAnswer] = useState(defaultDisarrangedText.disarranged);
  const disarrangedText = defaultDisarrangedText.disarranged.hanzi.split(' ')
  const defaultItems = disarrangedText.map((text, index) => {
    return {
      id: `item-${index}`,
      hanzi: text,
      pinyin: defaultDisarrangedText.disarranged.pinyin.split(' ')[index],
    }
  })
  const [items, setItems] = useState(defaultItems);
  const toolTipText = <TranslatedText id="render.question.rearrangetext.tooltip" />

  const onRearrange = (items) => {
    const newAnswer = items.map((item)=>item.hanzi).join(' ')
    setAnswer(newAnswer)
  }

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const updatedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(updatedItems)
  }

  useEffect(() => {
    onRearrange(items)
  }, [items])

  useEffect(() => {
    if (checkAnswer) {
      let arranged = defaultDisarrangedText.arranged.filter((arranged) => answer !== arranged.hanzi)
      if (arranged.length === defaultDisarrangedText.arranged.length) {
        setTileColor('dotted #DB2728')
        arranged = arranged.map((answer) => {
          return answer.hanzi.split(' ').map((hanzi, index) => {
            return {
              id: `item-${index}`,
              hanzi: hanzi,
              pinyin: answer.pinyin.split(' ')[index],
            }
          })
        })
        setArranged(arranged)
      } else {
        setTileColor('dotted #1C9E11')
      }

      const userArrangement = items.map((item) => item.hanzi).join(' ')
      onCheckAnswer(id, userArrangement, questionsKey);
    }
  }, [checkAnswer])

  useEffect(() => {
    const newItems = disarrangedText.map((text, index) => {
      return {
        id: `item-${index}`,
        hanzi: text,
        pinyin: defaultDisarrangedText.disarranged.pinyin.split(' ')[index],
      }
    })
    setItems(newItems)
  }, [defaultDisarrangedText])

  return (
    <FormItem
      style={{ textAlign: "center" }}
      colon={false}
    >
      {getFieldDecorator(id, {
        initialValue: defaultValue,
        rules: [
          {
            required,
            message: "Required Field"
          }
        ]
      })(
        <Fragment>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" direction="horizontal">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  style={getListStyle(snapshot.isDraggingOver)}
                  {...provided.droppableProps}
                >
                  <Row type="flex" justify="center" align="middle" gutter={[8, 0]}>
                  {items.map((item, index) => (
                    <Fragment key={index}>
                      <Draggable isDragDisabled={checkAnswer} key={item.id} draggableId={item.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                              tileColor,
                            )}
                          >
                            <Badge count={
                              <Tooltip placement="top" title={toolTipText}>
                                <Icon
                                  style={{ fontSize: '1em' }}
                                  type="question-circle"
                                />
                              </Tooltip>
                            }>
                              <Col>
                                <HanziTile
                                  hanzi={item.hanzi}
                                  pinyin={item.pinyin}
                                />
                              </Col>
                            </Badge>
                          </div>
                        )}
                      </Draggable>
                    </Fragment>
                  ))}
                  {provided.placeholder}
                  </Row>
                  {
                    checkAnswer && (
                      arranged.map((items, key) => {
                        return (
                          <Row key={key} type="flex" justify="center" align="middle" gutter={[8, 0]}>
                          {items.map((item, index) => (
                            <Fragment key={index}>
                              <Draggable isDragDisabled={checkAnswer} key={item.id} draggableId={item.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={getItemStyle(
                                      snapshot.isDragging,
                                      provided.draggableProps.style,
                                      'dotted #1C9E11',
                                    )}
                                  >
                                    <Col>
                                      <HanziTile
                                        hanzi={item.hanzi}
                                        pinyin={item.pinyin}
                                      />
                                    </Col>
                                  </div>
                                )}
                              </Draggable>
                            </Fragment>
                          ))}
                          {provided.placeholder}
                          </Row>
                        )
                      })
                    )
                  }
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Fragment>
      )}
    </FormItem>
  );
});
